import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { applyTheme, DARK_QUERY, readStoredTheme, resolveTheme, THEME_KEY, ThemeContext } from "./theme-context-value";
import type { ResolvedTheme, Theme, ThemeContextValue } from "./theme-context-value";

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);
  const [themeState, setThemeStateValue] = useState(() => {
    const initial = resolveTheme(readStoredTheme());
    applyTheme(initial);
    return { resolved: initial, resolvedVersion: 0 };
  });
  const { resolved, resolvedVersion } = themeState;

  const updateResolved = useCallback((next: ResolvedTheme) => {
    setThemeStateValue((current) => current.resolved === next
      ? current
      : { resolved: next, resolvedVersion: current.resolvedVersion + 1 });
  }, []);

  const update = useCallback((nextTheme: Theme, persist: boolean) => {
    setThemeState(nextTheme);
    const nextResolved = resolveTheme(nextTheme);
    updateResolved(nextResolved);
    applyTheme(nextResolved);
    if (!persist) return;
    try {
      if (nextTheme === "system") window.localStorage.removeItem(THEME_KEY);
      else window.localStorage.setItem(THEME_KEY, nextTheme);
    } catch {
      // Keep the in-memory preference even when storage is unavailable.
    }
  }, [updateResolved]);

  const setTheme = useCallback((nextTheme: Theme) => update(nextTheme, true), [update]);

  useEffect(() => {
    if (theme !== "system" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      const next: ResolvedTheme = event.matches ? "dark" : "light";
      updateResolved(next);
      applyTheme(next);
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [theme, updateResolved]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== THEME_KEY && event.key !== null) return;
      update(readStoredTheme(), false);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [update]);

  useEffect(() => applyTheme(resolved), [resolved]);

  const value = useMemo<ThemeContextValue>(() => ({ theme, resolved, resolvedVersion, setTheme }), [theme, resolved, resolvedVersion, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
